import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationsQuery,
  useGetProvincesQuery,
  useGetTagsQuery,
  useLazyGetOrganizationsQuery,
  useUpdateOrganizationMutation,
} from "reduxFiles/api/companyApi";
import { useLazyGetFormsQuery } from "reduxFiles/api/reportAndFormApi";
import { Organization, UserAssignment } from "types/companyTypes";
import { Column, TableAction } from "types/globalTypes";

const Organizations = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { data: provinces } = useGetProvincesQuery();
  const { data: tags } = useGetTagsQuery();

  // states
  const [item, setItem] = useState<Organization | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetOrganizationsQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters: filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateOrganizationMutation();
  const [updateApi, { isLoading: isUpdating }] =
    useUpdateOrganizationMutation();
  const [deleteApi, { isLoading: isDeleting }] =
    useDeleteOrganizationMutation();

  // search organizations
  const [triggerOrganizations] = useLazyGetOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // search forms
  const [triggerForms] = useLazyGetFormsQuery();
  const searchForms = async (text: string) => {
    try {
      const res = await triggerForms({
        page: 1,
        limit: 50,
        filters: { title: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.title,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form
  const fields: Field[] = [
    {
      name: "name",
      label: t("organization.name"),
      type: "text",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "provinceId",
      label: t("organization.provinceId"),
      type: "select",
      options: provinces?.map((opt) => ({
        label: opt.name,
        value: opt.id,
      })),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "needVerifyCompanies",
      label: t("organization.needVerifyCompanies"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "industrial",
      label: t("organization.industrial"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "tagId",
      label: t("organization.tagId"),
      type: "select",
      options: tags?.map((opt) => ({
        label: opt.name,
        value: opt.id,
      })),
      formed: true,
      filtered: true,
    },
    {
      name: "formId",
      label: t("organization.formId"),
      type: "select",
      onSearch: searchForms,
      formed: true,
      defaultValue: {
        default: {
          label: item?.form?.title,
          value: item?.form?.id,
        },
      },
    },
    {
      name: "parents",
      label: t("organization.parents"),
      type: "select",
      formed: true,
      multiple: true,
      onSearch: searchOrganizations,
      defaultSelectedOptions:
        item?.parents?.map((c) => ({
          label: c.name,
          value: c.id,
        })) || [],
    },
    {
      name: "children",
      label: t("organization.children"),
      type: "select",
      formed: true,
      multiple: true,
      onSearch: searchOrganizations,
      defaultSelectedOptions:
        item?.children?.map((c) => ({
          label: c.name,
          value: c.id,
        })) || [],
    },
    {
      name: "users",
      label: t("organization.users"),
      type: "userArray",
      formed: true,
      gridCol: 2,
    },
    {
      name: "hasForm",
      label: t("organization.formId"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      filtered: true,
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<Organization>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "name",
      title: t("organization.name"),
    },
    {
      key: "province",
      title: t("organization.provinceId"),
      render: (row) => row.province?.name || "-",
    },
    {
      key: "needVerifyCompanies",
      title: t("organization.needVerifyCompanies"),
      render: (row) =>
        row.needVerifyCompanies ? t("common.yes") : t("common.no"),
    },
    {
      key: "industrial",
      title: t("organization.industrial"),
      render: (row) => (row.industrial ? t("common.yes") : t("common.no")),
    },
    {
      key: "form",
      title: t("organization.formId"),
      render: (row) => row.form?.title || "-",
    },
    {
      key: "users",
      title: t("organization.users"),
      render: (row) =>
        row.users.length > 0
          ? row.users.map((u) => u.user?.fullname).join(" ,")
          : "-",
    },
  ];

  const actions: TableAction<Organization>[] = [
    {
      label: t("organization.editOrganization"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("organization.deleteOrganization"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      if (item) {
        await updateApi({ id: item.id, data: formData }).unwrap();
      } else {
        await addApi(formData).unwrap();
      }
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const getDefaultValues = () => {
    if (!item) {
      return {
        name: "",
        provinceId: "",
        needVerifyCompanies: "",
        industrial: "",
        tagId: "",
        formId: "",
        parents: [] as Organization[],
        children: [] as Organization[],
        users: [] as UserAssignment[],
      };
    }

    return {
      ...item,
      tagId: item.tag?.id,
      provinceId: item.province?.id || item.provinceId,
      needVerifyCompanies: `${item.needVerifyCompanies}`,
      industrial: `${item.industrial}`,
      parents: item.parents?.map((p) => p.id) || [],
      children: item.children?.map((c) => c.id) || [],
      formId: item.form?.id,
      users:
        item.users?.map((userAssignment) => ({
          userId: userAssignment.userId,
          default: {
            label: userAssignment.user?.fullname ?? "-",
            value: userAssignment.userId,
          },
          provinceIds: userAssignment.provinces?.map((p) => p.id) || [],
          industrialIds: userAssignment.industrials?.map((ind) => ind.id) || [],
        })) || [],
    };
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("organization.organizationList")}
          createBtn={{
            title: t("organization.addOrganization"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<Organization>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={getDefaultValues()}
          title={
            item
              ? t("organization.editOrganization")
              : t("organization.addOrganization")
          }
          isEdit={!!item}
          loading={item ? isUpdating : isAdding}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("organization.deleteOrganization")}
          desc={t("actions.confirmDeleteDesc", { name: item?.name || "" })}
        />
      </div>
    </div>
  );
};

export default Organizations;
